import { useCallback, useEffect, useState } from "react";
import { CalendarCheck, Plus, X, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg } from "@/lib/api";
import { useMember } from "@/context/MemberContext";
import { fmt, toISO } from "@/lib/dates";
import { WEEKDAYS } from "@/lib/availability";
import { PageHeader, Panel, Loader } from "@/components/common";
import { TeamAvailability } from "@/components/TeamAvailability";

const DayRow = ({ day, onChange }) => (
  <div className="flex flex-wrap items-center gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`avail-day-${day.weekday}`}>
    <span className="w-24 font-medium">{WEEKDAYS[day.weekday]}</span>
    <Switch checked={day.available} onCheckedChange={(v) => onChange({ ...day, available: v })} data-testid={`avail-day-${day.weekday}-switch`} />
    <span className={`text-sm ${day.available ? "text-emerald-700" : "text-slate-400"}`}>{day.available ? "Beschikbaar" : "Niet beschikbaar"}</span>
    {day.available && (
      <div className="flex items-center gap-2 ml-auto">
        <Input type="time" value={day.start} onChange={(e) => onChange({ ...day, start: e.target.value })} className="w-28 h-9" aria-label="Vanaf" data-testid={`avail-day-${day.weekday}-start`} />
        <span className="text-slate-400 text-sm">tot</span>
        <Input type="time" value={day.end} onChange={(e) => onChange({ ...day, end: e.target.value })} className="w-28 h-9" aria-label="Tot" data-testid={`avail-day-${day.weekday}-end`} />
        {(day.start || day.end) && (
          <button onClick={() => onChange({ ...day, start: "", end: "" })} className="text-xs text-slate-500 underline" data-testid={`avail-day-${day.weekday}-clear`}>hele dag</button>
        )}
      </div>
    )}
  </div>
);

const DatesOff = ({ dates, onChange }) => {
  const [d, setD] = useState("");
  const [reason, setReason] = useState("");
  const add = () => {
    if (!d) return;
    onChange([...dates.filter((x) => x.date !== d), { date: d, reason }].sort((a, b) => a.date.localeCompare(b.date)));
    setD("");
    setReason("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 items-end">
        <div><Label htmlFor="off-date">Datum</Label><Input id="off-date" type="date" min={toISO(new Date())} value={d} onChange={(e) => setD(e.target.value)} className="mt-1.5 w-44" data-testid="avail-off-date-input" /></div>
        <div className="flex-1 min-w-[160px]"><Label htmlFor="off-reason">Reden (optioneel)</Label><Input id="off-reason" value={reason} maxLength={80} onChange={(e) => setReason(e.target.value)} placeholder="Bijv. vakantie, school" className="mt-1.5" data-testid="avail-off-reason-input" /></div>
        <Button variant="outline" onClick={add} disabled={!d} data-testid="avail-off-add-button"><Plus className="h-4 w-4 mr-1" />Toevoegen</Button>
      </div>
      <p className="text-xs text-slate-500 mt-2">Vul geen medische of andere gevoelige redenen in.</p>
      <ul className="mt-3 flex flex-wrap gap-2" data-testid="avail-off-list">
        {dates.map((x) => (
          <li key={x.date} className="inline-flex items-center gap-2 rounded-full bg-[#FFECE8] text-[#9A2B12] px-3 py-1 text-xs font-semibold" data-testid={`avail-off-${x.date}`}>
            {fmt(x.date, "EEE d MMM")}{x.reason && ` · ${x.reason}`}
            <button onClick={() => onChange(dates.filter((y) => y.date !== x.date))} aria-label="Verwijderen" data-testid={`avail-off-remove-${x.date}`}><X className="h-3.5 w-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function Availability() {
  const api = useApi();
  const { isManager } = useMember();
  const [av, setAv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [teamKey, setTeamKey] = useState(0);

  const load = useCallback(() => api.get("/availability/me").then((r) => setAv(r.data)), [api]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/availability/me", { days: av.days, unavailable_dates: av.unavailable_dates, note: av.note });
      setAv(data);
      setTeamKey((k) => k + 1);
      toast.success("Beschikbaarheid opgeslagen");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!av) return <Loader />;
  const setDay = (day) => setAv({ ...av, days: av.days.map((x) => (x.weekday === day.weekday ? day : x)) });

  return (
    <div data-testid="availability-page">
      <PageHeader title="Beschikbaarheid" subtitle="Geef aan wanneer je kunt werken. Je planner houdt hier rekening mee.">
        <Button className="rounded-full" onClick={save} disabled={busy} data-testid="avail-save-button"><CalendarCheck className="h-4 w-4 mr-2" />Opslaan</Button>
      </PageHeader>
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-4 items-start">
        <Panel className="fade-up" data-testid="avail-week-panel">
          <h2 className="font-semibold text-lg">Vaste week</h2>
          <p className="text-sm text-slate-500 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" />Laat de tijden leeg als je de hele dag kunt.</p>
          <div className="mt-2">{av.days.map((d) => <DayRow key={d.weekday} day={d} onChange={setDay} />)}</div>
        </Panel>
        <div className="space-y-4">
          <Panel className="fade-up fade-up-2" data-testid="avail-dates-panel">
            <h2 className="font-semibold text-lg mb-3">Vrije dagen</h2>
            <DatesOff dates={av.unavailable_dates} onChange={(v) => setAv({ ...av, unavailable_dates: v })} />
          </Panel>
          <Panel className="fade-up fade-up-3">
            <Label htmlFor="avail-note">Opmerking voor je planner</Label>
            <Textarea id="avail-note" value={av.note} maxLength={300} onChange={(e) => setAv({ ...av, note: e.target.value })} rows={2} placeholder="Bijv. max 3 avonden per week" className="mt-1.5 resize-none" data-testid="avail-note-input" />
          </Panel>
        </div>
      </div>
      {isManager && <TeamAvailability key={teamKey} />}
    </div>
  );
}
